/**
 * Audit Logs Table Component
 *
 * Displays audit logs in a table format with badges for categories and severity.
 * Supports row click to open detail modal.
 */

import { useState } from 'react';
import { format } from 'date-fns';
import { Eye, AlertCircle, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AuditLogDetailModal } from './audit-log-detail-modal';
import type { AuditLog } from '@/types/audit-log';
import {
  AUDIT_LOG_CATEGORY_COLORS,
  AUDIT_LOG_CATEGORY_LABELS,
  AUDIT_LOG_SEVERITY_COLORS,
  AUDIT_LOG_SEVERITY_LABELS,
  CACHE_STATUS_COLORS,
  CACHE_STATUS_LABELS,
  getStatusCodeColor,
} from '@/types/audit-log';
import { cn } from '@/lib/utils';

/**
 * Audit Logs Table Props
 */
interface AuditLogsTableProps {
  /** Array of audit log entries */
  logs: AuditLog[];
  /** Loading state */
  isLoading?: boolean;
  /** Error state */
  error?: Error | null;
  /** Retry callback for errors */
  onRetry?: () => void;
}

/**
 * Format Date Time
 * Formats ISO date string to readable format
 */
function formatDateTime(dateString: string): string {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
  } catch {
    return dateString;
  }
}

/**
 * Truncate Text
 * Truncates long text with ellipsis
 */
function truncate(text: string | null | undefined, maxLength = 20): string {
  if (!text) return '-';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
}

/**
 * Loading State
 */
function LoadingState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-2">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading audit logs...</p>
    </div>
  );
}

/**
 * Empty State
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12 space-y-2">
      <AlertCircle className="h-12 w-12 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">No audit logs found</p>
      <p className="text-xs text-muted-foreground">Try adjusting your filters or date range</p>
    </div>
  );
}

/**
 * Audit Logs Table Component
 */
export function AuditLogsTable({
  logs,
  isLoading = false,
  error = null,
  onRetry,
}: AuditLogsTableProps) {
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  /**
   * Handle Row Click
   * Opens detail modal for the selected log
   */
  const handleRowClick = (log: AuditLog) => {
    setSelectedLog(log);
  };

  /**
   * Handle Modal Close
   * Closes the detail modal
   */
  const handleModalClose = () => {
    setSelectedLog(null);
  };

  // Error State
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-4">
        <AlertCircle className="h-12 w-12 text-destructive" />
        <div className="text-center">
          <h3 className="text-lg font-semibold">Failed to load audit logs</h3>
          <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Loading State
  if (isLoading) {
    return <LoadingState />;
  }

  // Empty State
  if (logs.length === 0) {
    return <EmptyState />;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Occurred At</TableHead>
              <TableHead>Event Type</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>User ID</TableHead>
              <TableHead>IP Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Cache</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map(log => (
              <TableRow
                key={log.id}
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => handleRowClick(log)}
              >
                {/* Occurred At */}
                <TableCell className="font-mono text-xs">
                  {formatDateTime(log.occurredAt)}
                </TableCell>

                {/* Event Type */}
                <TableCell className="font-medium">{truncate(log.eventType, 30)}</TableCell>

                {/* Category Badge */}
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn('font-medium', AUDIT_LOG_CATEGORY_COLORS[log.category])}
                  >
                    {AUDIT_LOG_CATEGORY_LABELS[log.category]}
                  </Badge>
                </TableCell>

                {/* Severity Badge */}
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn('font-medium', AUDIT_LOG_SEVERITY_COLORS[log.severity])}
                  >
                    {AUDIT_LOG_SEVERITY_LABELS[log.severity]}
                  </Badge>
                </TableCell>

                {/* User ID */}
                <TableCell className="font-mono text-xs">{truncate(log.userId, 15)}</TableCell>

                {/* IP Address */}
                <TableCell className="font-mono text-xs">{log.ipAddress}</TableCell>

                {/* Status Code */}
                <TableCell>
                  <span
                    className={cn(
                      'font-mono text-sm font-medium',
                      getStatusCodeColor(log.statusCode)
                    )}
                  >
                    {log.statusCode || '-'}
                  </span>
                </TableCell>

                {/* Cache Status */}
                <TableCell>
                  <Badge
                    variant="outline"
                    className={cn(
                      'font-medium text-xs',
                      log.cacheStatus
                        ? CACHE_STATUS_COLORS[log.cacheStatus]
                        : CACHE_STATUS_COLORS['na']
                    )}
                  >
                    {log.cacheStatus
                      ? CACHE_STATUS_LABELS[log.cacheStatus]
                      : CACHE_STATUS_LABELS['na']}
                  </Badge>
                </TableCell>

                {/* Actions */}
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      handleRowClick(log);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Detail Modal */}
      <AuditLogDetailModal
        log={selectedLog}
        isOpen={selectedLog !== null}
        onClose={handleModalClose}
      />
    </>
  );
}
